<template>
    <div>
        <app-template>
            <div class="space-y-6">

                <!-- Header -->
                <div class="md:flex items-center justify-between">
                    <h1 class="text-2xl font-bold text-gray-800 mb-4 md:mb-0">
                        Gestion des bouteilles de gaz
                    </h1>

                    <div class="flex items-center gap-2">
                        <button v-if="!$account.account.societe.solo" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center" @click="openExportModal()">
                            <i class="fas fa-file-csv mr-2"></i>
                            Exporter CSV
                        </button>
                        <button
                            @click="openCreate"
                            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
                        >
                            <i class="fas fa-plus mr-2"></i>
                            Nouvelle bouteille
                        </button>
                    </div>
                </div>

                <!-- Filtres -->
                <div class="bg-white border rounded-lg p-4 shadow-sm">
                    <div class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">

                        <input
                            v-model="filters.numero"
                            placeholder="Numéro"
                            class="border rounded px-3 py-2 focus:outline-none"
                        />

                        <div class="relative group">
                            <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"></i>
                            <input
                                v-model="searchFilter"
                                @input="filterFluidesFilter"
                                @focus="showDropdownFilters = true"
                                type="text"
                                id="inp-type-gaz-filter"
                                placeholder="Type de fluide (ex: R32...)"
                                class="pl-10 pr-4 py-2.5 rounded w-full focus:outline-none border border-gray-200"
                            />
                            <ul v-if="showDropdownFilters && filteredFluidesFilter.length" class="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto py-2">
                                <li
                                    v-for="fluide in filteredFluidesFilter"
                                    :key="fluide.code"
                                    @mousedown.prevent="selectFluideFilter(fluide)"
                                    class="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    <div class="flex justify-between items-center">
                                        <span class="font-black text-gray-900">{{ fluide.code }}</span>
                                        <span class="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{{ fluide.type }}</span>
                                    </div>
                                    <div class="text-xs text-gray-400 mt-1">{{ fluide.nom }}</div>
                                </li>
                            </ul>
                        </div>

                        <select v-model="filters.etat" class="border rounded px-3 py-2 focus:outline-none">
                            <option :value="null">Tous types</option>
                            <option value="charge">Charge</option>
                            <option value="récupération">Récupération</option>
                        </select>

                        <select v-model="filters.emplacement_id" class="border rounded px-3 py-2 focus:outline-none">
                            <option :value="null">Tous emplacements</option>
                            <option v-for="e in emplacements" :key="e.id" :value="e.id">
                                {{ e.name }}
                            </option>
                        </select>

                        <select v-model="filters.is_recycle" class="border rounded px-3 py-2 focus:outline-none">
                            <option :value="3">Toutes les bouteilles</option>
                            <option :value="1">Bouteilles actives</option>
                            <option :value="2">Bouteilles recyclées</option>
                        </select>

                    </div>
                </div>

                <!-- No results found -->
                <div v-if="!loadingList && bouteilles && bouteilles.length <= 0" class="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-12 text-center shadow-lg">
                    <div class="flex flex-col items-center">
                        <div class="bg-blue-100 rounded-full p-6">
                            <i class="fas fa-inbox text-5xl text-blue-600"></i>
                        </div>
                        <h3 class="text-2xl font-bold text-gray-800 mt-4">Aucune bouteille trouvée</h3>
                        <p class="text-gray-600 max-w-md mt-1">
                            Essayez de modifier les filtres ou créez une nouvelle bouteille.
                        </p>
                    </div>
                </div>

                <!-- Liste -->
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    <!-- Skeleton loading -->
                    <template v-if="loadingList">
                        <div
                            v-for="i in 3"
                            :key="'skeleton-' + i"
                            class="bg-white border rounded-lg p-4 shadow-sm"
                        >
                            <!-- Header -->
                            <div class="flex justify-between items-start">
                                <div class="space-y-2">
                                    <div class="h-4 w-32 bg-gray-200 rounded"></div>
                                    <div class="h-3 w-40 bg-gray-100 rounded"></div>
                                </div>
                                <div class="h-5 w-5 bg-gray-200 rounded"></div>
                            </div>

                            <div class="flex gap-4 mt-4">
                                <!-- Bottle -->
                                <div class="w-[85px] flex flex-col items-center">
                                    <div class="h-[140px] w-[70px] bg-gray-200 rounded-xl"></div>
                                    <div class="h-4 w-10 bg-gray-200 rounded mt-3"></div>
                                    <div class="h-3 w-16 bg-gray-100 rounded mt-1"></div>
                                </div>

                                <!-- Infos -->
                                <div class="flex-1 space-y-3 mt-2">
                                    <div class="h-3 w-40 bg-gray-200 rounded"></div>

                                    <!-- Buttons -->
                                    <div class="flex flex-wrap gap-2 mt-4">
                                        <div class="h-6 w-20 bg-gray-100 rounded"></div>
                                        <div class="h-6 w-24 bg-gray-100 rounded"></div>
                                        <div class="h-6 w-24 bg-gray-100 rounded"></div>
                                        <div class="h-6 w-20 bg-gray-100 rounded"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-if="!loadingList">
                        <div
                            v-for="bouteille in bouteilles"
                            :key="bouteille.id"
                            class="bg-white border rounded-lg p-4 shadow-sm"
                        >
                            <div class="flex justify-between items-start">
                                <div>
                                    <p class="font-bold text-gray-800">{{ bouteille.numero }}</p>
                                    <p class="text-sm text-gray-500">
                                        {{ bouteille.type_gaz }} · {{ bouteille.etat === 'charge' ? 'Bouteille de charge' : 'Bouteille de récupération' }}
                                    </p>
                                </div>

                                <button
                                    @click="openEdit(bouteille)"
                                    class="text-gray-400 hover:text-blue-600"
                                >
                                    <i class="fas fa-edit"></i>
                                </button>
                            </div>

                            <div class="flex gap-4">
                                <!-- Barre -->
                                <div class="mt-4">
                                    <!-- Conteneur de la bouteille SVG -->
                                    <div class="relative w-[85px]">
                                        <svg viewBox="0 0 100 125" class="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMid meet">
                                            <defs>
                                                <!-- Le masque ne contient MAINTENANT QUE le corps de la bouteille -->
                                                <mask id="bodyMask">
                                                    <path d="M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45 L75,110 C75,118 68,125 60,125 L40,125 C32,125 25,118 25,110 Z" fill="white" />
                                                </mask>
                                            </defs>

                                            <!-- 1. Fond Gris (Bouteille vide) -->
                                            <!-- Poignée -->
                                            <path d="M35,30 L35,15 C35,10 40,5 50,5 C60,5 65,10 65,15 L65,30" stroke="#E5E7EB" stroke-width="8" fill="none" />
                                            <!-- Corps -->
                                            <path d="M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45 L75,110 C75,118 68,125 60,125 L40,125 C32,125 25,118 25,110 Z" fill="#E5E7EB" />

                                            <!-- 2. Remplissage dynamique (Limité au masque du corps uniquement) -->
                                            <g mask="url(#bodyMask)">
                                                <rect 
                                                    x="0" 
                                                    :y="125 - (bouteille.pourcentage_remplissage * 0.95)" 
                                                    width="100" 
                                                    height="100" 
                                                    :class="bouteille.etat == 'récupération' ? 'fill-yellow-500' : (bouteille.pourcentage_remplissage < 20 ? 'fill-red-500' : 'fill-green-500')"
                                                    style="transition: y 0.6s cubic-bezier(0.4, 0, 0.2, 1), fill 0.5s;"
                                                />
                                            </g>

                                            <!-- 3. Contours Noirs (Finition) -->
                                            <!-- Poignée -->
                                            <path d="M35,30 L35,15 C35,10 40,5 50,5 C60,5 65,10 65,15 L65,30" stroke="black" stroke-width="5" fill="none" stroke-linecap="round" />
                                            <!-- Corps -->
                                            <path d="M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45 L75,110 C75,118 68,125 60,125 L40,125 C32,125 25,118 25,110 Z" stroke="black" stroke-width="5" fill="none" />
                                            
                                            <!-- Détail Valve -->
                                            <rect x="42" y="22" width="16" height="10" rx="2" fill="black" />
                                        </svg>
                                    </div>
                                    <div class="text-center text-sm mt-3">
                                        <span class="font-bold" :class="bouteille.etat == 'récupération' ? 'text-slate-800' : (bouteille.pourcentage_remplissage < 20 ? 'text-red-600' : 'text-green-600')">
                                            {{ bouteille.pourcentage_remplissage }}%
                                        </span>
                                        <p class="text-xs">{{ bouteille.poids_actuel }} / {{ bouteille.poids_max }} kg</p>
                                    </div>
                                </div>

                                <div>
                                    <!-- Infos -->
                                    <div class="mt-3 text-sm text-gray-600 space-y-1">
                                        <p v-if="!bouteille.is_recycle && bouteille.emplacement"><i class="fas fa-map-marker-alt w-5"></i> Emplacement {{ bouteille.emplacement.name || '—' }}</p>
                                    </div>

                                    <div v-if="bouteille.is_recycle" class="mt-6 bg-green-600 text-white rounded p-4">
                                        <p class="text-lg font-bold"><i class="fas fa-recycle w-5"></i> Bouteille recyclée</p>
                                        <p class="text-sm font-light">Trackdéchets: {{ bouteille.num_track_dechets || '—' }}</p>
                                    </div>
        
                                    <!-- Actions -->
                                    <div v-if="!bouteille.is_recycle" class="mt-4 flex flex-wrap gap-2">
                                        <button
                                            @click="openGaz(bouteille)"
                                            class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded"
                                        >
                                            <i class="fas fa-exchange-alt mr-1"></i> Gaz
                                        </button>
        
                                        <button
                                            @click="openEmplacement(bouteille)"
                                            class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded"
                                        >
                                            <i class="fas fa-warehouse mr-1"></i> Emplacement
                                        </button>
        
                                        <button
                                            @click="openMovements(bouteille)"
                                            class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded"
                                        >
                                            <i class="fas fa-history mr-1"></i> Historique
                                        </button>

                                        <button
                                            @click="openRecycle(bouteille)"
                                            class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded"
                                        >
                                            <i class="fas fa-recycle mr-1"></i> Recyclage
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </app-template>

        <!-- MODALES -->
        <modal name="bouteille-form" height="auto" width="700" :scrollable="true" :adaptive="true">
            <div class="p-8 space-y-6 overflow-y-auto max-h-[100vh]">
            <div class="border-b pb-4 flex items-center justify-between gap-10">
                <div>
                    <h2 class="text-2xl font-bold text-gray-800">
                    {{ form.id ? 'Modifier la bouteille' : 'Nouvelle bouteille' }}
                    </h2>
                    <p class="text-sm text-gray-500 mt-1">Renseignez les informations de la bouteille de gaz</p>
                </div>
                <div>
                    <button @click="$modal.hide('bouteille-form')" class="text-sm text-gray-500"><i class="fas fa-times text-red-500 text-lg"></i></button>
                </div>
            </div>

            <div class="space-y-5">
                <!-- Informations principales -->
                <div>
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Numéro de bouteille <span class="text-red-500 font-bold">*</span></label>
                        <input 
                            v-model="form.numero" 
                            placeholder="Ex: B-001" 
                            class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition" 
                            id="inp-numero" 
                        />
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Type de gaz <span class="text-red-500 font-bold">*</span></label>
                            <div class="relative group">
                                <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"></i>
                                <input
                                    v-model="search"
                                    @input="filterFluides"
                                    @focus="showDropdown = true"
                                    type="text"
                                    id="inp-type-gaz"
                                    placeholder="Rechercher un fluide (ex: R32...)"
                                    :class="showErrors && (!form.type_gaz || !selectedFluide) ? 'border-red-500 bg-red-50' : 'border-transparent'"
                                    class="border border-gray-300 pl-10 pr-4 py-2.5 rounded-lg w-full focus:outline-none transition"
                                />
                                <ul v-if="showDropdown && filteredFluides.length" class="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto py-2">
                                    <li
                                        v-for="fluide in filteredFluides"
                                        :key="fluide.code"
                                        @mousedown.prevent="selectFluide(fluide)"
                                        class="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <div class="flex justify-between items-center">
                                            <span class="font-black text-gray-900">{{ fluide.code }}</span>
                                            <span class="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{{ fluide.type }}</span>
                                        </div>
                                        <div class="text-xs text-gray-400 mt-1">{{ fluide.nom }}</div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Type de bouteille <span class="text-red-500 font-bold">*</span></label>
                            <select 
                                v-model="form.etat" 
                                class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition" 
                                id="inp-type"
                            >
                                <option value="charge">Bouteille de charge</option>
                                <option value="récupération">Bouteille de récupération</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">
                                <template v-if="form.etat === 'charge'">
                                    Quantité de gaz à l'achat (kg) <span class="text-red-500 font-bold">*</span>
                                </template>
                                <template v-if="form.etat === 'récupération'">
                                    Capacité maximale de récupération (kg) <span class="text-red-500 font-bold">*</span>
                                </template>
                            </label>
                            <input 
                                v-model.number="form.poids_max" 
                                type="number" 
                                placeholder="Ex: 50" 
                                class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition" 
                                id="inp-poids-max" 
                            />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Quantité de gaz actuel dans la bouteille (kg)</label>
                            <input 
                                v-model.number="form.poids_actuel" 
                                type="number" 
                                placeholder="Ex: 50" 
                                class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition" 
                                id="inp-poids-actuel" 
                            />
                        </div>
                    </div>
                    </div>

                    <!-- Emplacement -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Emplacement de stockage</label>
                        <select 
                            v-model="form.emplacement_id" 
                            class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition"
                        >
                            <option :value="null" disabled>Sélectionner un emplacement</option>
                            <option v-for="loc in emplacements" :key="loc.id" :value="loc.id">
                                {{ loc.name }}
                            </option>
                        </select>
                    </div>

                    <!-- Adresse de recyclage -->
                    <div class="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-200">
                        <div class="flex items-center gap-2 mb-3">
                            <i class="fas fa-recycle text-blue-600"></i>
                            <h3 class="text-sm font-semibold text-gray-800">Adresse de recyclage (TrackDéchets) <span class="text-red-500 font-bold">*</span></h3>
                        </div>
                        <div class="text-sm bg-yellow-400 text-yellow-800 p-4 rounded mb-2" v-if="form?.id && form.poids_max > 0 && form.etat === 'charge'">
                            <i class="fas fa-exclamation-triangle mr-1"></i> Cette adresse est sans doute utilisée sur des CERFA. En la modifiant, les CERFA liés ne seront pas mis à jour automatiquement.
                        </div>

                        <div v-if="adressesList" class="mt-4">
                            <div v-for="(adresse, adresseIndex) in adressesList" :key="adresseIndex">
                                <div class="border border-gray-300 rounded-lg p-4 mb-2 bg-white" v-if="adresse">
                                    <div class="flex items-center justify-between">
                                        <div class="font-medium text-gray-800 text-sm">{{ adresse.adresse1 + ', ' + adresse.code_postal + ' ' + adresse.ville }}</div>
                                        <button  @click="form.recuperation_adresse_id = adresse.id"  :class="form.recuperation_adresse_id === adresse.id ? '' : 'bg-slate-200 hover:bg-slate-300 duration-200 px-4 py-2 rounded text-sm font-light'" type="button">
                                            <template v-if="form.recuperation_adresse_id !== adresse.id">
                                                Utiliser cette adresse
                                            </template>
                                            <template v-else>
                                                <div class="text-green-500 font-light text-sm">
                                                    Adresse sélectionnée <i class="fas fa-check-circle ml-1 text-lg"></i>
                                                </div>
                                            </template>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="adressesList.length > 0" class="flex items-center gap-2 mt-4">
                            <input type="checkbox" id="chk-new-adresse" v-model="displayNewAdresse" @input="form.recuperation_adresse_id = null" />
                            <label for="chk-new-adresse" class="text-sm font-medium text-gray-700">Ajouter une nouvelle adresse de recyclage</label>
                        </div>
                        <div class="bg-white p-4 rounded-lg border mt-4" v-show="displayNewAdresse">
                            <adresse-form v-model="formAdresseRecup" :min="true"></adresse-form>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="flex justify-end gap-3 pt-4 border-t">
                    <button 
                    @click="$modal.hide('bouteille-form')" 
                    class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg font-medium transition"
                    >
                        Annuler
                    </button>
                    <button 
                    @click="submitForm" 
                    class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium transition shadow-sm"
                    :disabled="loadingEditAddBouteille"
                    >
                        <template v-if="loadingEditAddBouteille">
                            <i class="fas fa-spinner fa-spin"></i>
                        </template>
                        <template v-if="!loadingEditAddBouteille">
                            <i class="fas fa-save mr-2"></i>
                            Enregistrer
                        </template>
                    </button>
                </div>
            </div>
        </modal>

        <modal name="gaz-form" height="auto" width="400">
            <div class="p-6 space-y-4">
                <h2 class="text-lg font-bold">Mouvement de gaz</h2>

                <template v-if="loadingAddGaz">
                    <div class="flex justify-center items-center py-8">
                        <i class="fas fa-spinner fa-spin text-3xl"></i>
                    </div>
                </template>

                <template v-if="!loadingAddGaz">
                    <div v-if="current">
                        <label for="inp-qtt-gaz">Quantité à {{ current.etat == 'charge' ? 'retirer' : 'ajouter' }} dans la bouteille (kg)</label>
                        <input
                            v-model.number="gaz.quantite"
                            type="number"
                            placeholder="kg"
                            class="border rounded px-3 py-2 w-full mt-1 focus:outline-none"
                            id="inp-qtt-gaz"
                        />
                    </div>

                    <div class="flex justify-end gap-2">
                        <button @click="$modal.hide('gaz-form')" class="bg-gray-200 px-4 py-2 rounded">Annuler</button>
                        <button @click="submitGaz" class="bg-blue-600 text-white px-4 py-2 rounded">Valider</button>
                    </div>
                </template>
            </div>
        </modal>

        <modal name="emplacement-form" height="auto" width="400">
            <div class="p-6 space-y-4">
                <h2 class="text-lg font-bold">Changer d’emplacement</h2>

                <select v-model="emplacement.emplacement_id" class="border rounded px-3 py-2 w-full focus:outline-none">
                    <option v-for="loc in emplacements" :key="loc.id" :value="loc.id">
                        {{ loc.name }}
                    </option>
                </select>

                <div class="flex justify-end gap-2">
                    <template v-if="loadingChangeEmplacement">
                        <i class="fas fa-spinner fa-spin"></i>
                    </template>
                    <template v-if="!loadingChangeEmplacement">
                        <button @click="$modal.hide('emplacement-form')" class="bg-gray-200 px-4 py-2 rounded">Annuler</button>
                        <button @click="submitEmplacement" class="bg-blue-600 text-white px-4 py-2 rounded">Valider</button>
                    </template>
                </div>
            </div>
        </modal>

        <modal name="movements" height="auto" width="600">
            <div class="p-6">
                <div v-if="loadingGetMovements">
                    <i class="fas fa-spinner fa-spin"></i> Chargement...
                </div>
                <div v-if="!loadingGetMovements">
                    <h2 class="text-lg font-bold mb-2">Historique des mouvements</h2>

                    <button @click="exportMovementsCsv" class="bg-slate-200 hover:bg-slate-300 duration-200 px-4 py-2 rounded text-xs font-light mb-4"><i class="fas fa-file-export mr-1"></i> Exporter</button>

                    <table class="w-full text-sm">
                        <thead>
                            <tr class="border bg-gray-100">
                                <th class="text-left py-2 px-2 border">Action</th>
                                <th class="text-left py-2 px-2 border">Détails</th>
                                <th class="text-left py-2 px-2 border">Utilisateur</th>
                                <th class="text-left py-2 px-2 border">Date</th>
                                <th class="text-left py-2 px-2 border">CERFA</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="m in movements" :key="m.id" class="border">
                                <td class="py-2 px-2 border font-semibold">{{ m.action }}</td>
                                <td class="py-2 px-2 border">{{ m.details }}</td>
                                <td class="py-2 px-2 border">
                                    <template v-if="m.account">
                                        {{ m.account.prenom + ' ' + m.account.nom }}
                                    </template>
                                    <template v-else>
                                        -
                                    </template>
                                </td>
                                <td class="py-2 px-2 border">{{ new Date(m.created_at).toLocaleString() }}</td>
                                <td class="py-2 px-2 border">
                                    <template v-if="m.cerfa">
                                        <a class="text-xs text-blue-600 hover:underline" target="_blank" :href="m.cerfa.document_url">Consulter</a>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </modal>

        <modal name="recycle-form" height="auto" width="400">
            <div class="p-6 space-y-4">
                <h2 class="text-lg font-bold">Cette bouteille est recyclée</h2>

                <template v-if="(current && current.etat == 'charge' && current.poids_actuel != 0)">
                    <div class="bg-yellow-100 text-yellow-800 p-4 rounded">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        La bouteille de charge n'est pas vide <br />
                        <span class="text-xs font-light">{{ current.poids_actuel }} kg restants</span>
                    </div>
                </template>

                <template v-if="(current && current.etat == 'récupération' && current.poids_actuel != current.poids_total)">
                    <div class="bg-yellow-100 text-yellow-800 p-4 rounded">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        La bouteille n'est pas complétement pleine.<br />
                    </div>
                </template>

                <template v-if="loadingTrackDechets">
                    <div class="flex justify-center items-center py-8">
                        <i class="fas fa-spinner fa-spin text-3xl"></i>
                    </div>
                </template>
                <template v-if="!loadingTrackDechets">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Numéro de suivi des déchets (TrackDéchets)</label>
                        <input v-model="numTrackDechets" class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition" id="inp-track-dechets" />
                    </div>

                    <div class="flex justify-end gap-2">
                        <button @click="$modal.hide('recycle-form')" class="bg-gray-200 px-4 py-2 rounded">Annuler</button>
                        <button @click="submitRecycle" class="bg-blue-600 text-white px-4 py-2 rounded">Valider</button>
                    </div>
                </template>
            </div>
        </modal>

        <!-- Export CSV -->
        <modal name="export-csv-modal" height="auto" :adaptive="true">
            <div class="p-6">
                <h2 class="text-lg font-bold mb-4">
                    Export des bouteilles (CSV)
                </h2>

                <div class="space-y-4">
                    <!-- Type d’export -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Type d’export</label>
                        <select v-model="exportFilters.type_export" class="w-full border rounded px-3 py-2">
                            <option value="mouvements">Mouvements des fluides</option>
                            <option value="bouteilles">Bouteilles</option>
                        </select>
                    </div>

                    <!-- Numéro de bouteille -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Numéro de bouteille</label>
                        <input
                            v-model="exportFilters.numero"
                            type="text"
                            placeholder="Ex : BT-001"
                            class="w-full border rounded px-3 py-2"
                        >
                    </div>

                    <!-- Type de gaz -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Type de gaz</label>
                        <div class="relative group">
                            <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"></i>
                            <input
                                v-model="search"
                                @input="filterFluides"
                                @focus="showDropdown = true"
                                type="text"
                                id="inp-type-gaz"
                                placeholder="Rechercher un fluide (ex: R32...)"
                                :class="showErrors && (!form.type_gaz || !selectedFluide) ? 'border-red-500 bg-red-50' : 'border-transparent'"
                                class="border border-gray-300 pl-10 pr-4 py-2.5 rounded-lg w-full focus:outline-none transition"
                            />
                            <ul v-if="showDropdown && filteredFluides.length" class="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto py-2">
                                <li
                                    v-for="fluide in filteredFluides"
                                    :key="fluide.code"
                                    @mousedown.prevent="selectFluide(fluide)"
                                    class="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    <div class="flex justify-between items-center">
                                        <span class="font-black text-gray-900">{{ fluide.code }}</span>
                                        <span class="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{{ fluide.type }}</span>
                                    </div>
                                    <div class="text-xs text-gray-400 mt-1">{{ fluide.nom }}</div>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- État / type de bouteille -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Type de bouteille</label>
                        <select v-model="exportFilters.etat" class="w-full border rounded px-3 py-2">
                            <option :value="null">Tous</option>
                            <option v-for="etat in etatList" :key="etat" :value="etat">
                                {{ etat }}
                            </option>
                        </select>
                    </div>

                    <!-- Emplacement -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Emplacement</label>
                        <select v-model="exportFilters.emplacement_id" class="w-full border rounded px-3 py-2">
                            <option :value="null">Tous</option>
                            <option
                                v-for="emp in emplacements"
                                :key="emp.id"
                                :value="emp.id"
                            >
                                {{ emp.name }}
                            </option>
                        </select>
                    </div>

                    <!-- Recyclage -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Recyclage</label>
                        <select v-model="exportFilters.is_recycle" class="w-full border rounded px-3 py-2">
                            <option :value="null">Toutes</option>
                            <option :value="1">Recyclées</option>
                            <option :value="0">Non recyclées</option>
                        </select>
                    </div>

                    <!-- Date début -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Date début</label>
                        <input
                            v-model="exportFilters.date_debut"
                            type="date"
                            class="w-full border rounded px-3 py-2"
                        >
                    </div>

                    <!-- Date fin -->
                    <div>
                        <label class="block text-sm font-medium mb-1">Date fin</label>
                        <input
                            v-model="exportFilters.date_fin"
                            type="date"
                            class="w-full border rounded px-3 py-2"
                        >
                    </div>
                </div>


                <div class="flex justify-end mt-6 space-x-3">
                    <button class="px-4 py-2 rounded border" @click="$modal.hide('export-csv-modal')">
                        Annuler
                    </button>
                    <button class="bg-green-600 text-white px-4 py-2 rounded flex items-center" @click="exportCsv" :disabled="loadingExport">
                        <i v-if="loadingExport" class="fas fa-spinner fa-spin mr-2"></i>
                        Exporter
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import axios from 'axios'
import env from '@/services/env.service'

import AdresseForm from '../Client/Components/AdresseForm.vue';
import fluidesList from '@/config/fluides';

export default {
    components: {
        AdresseForm,
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                clearTimeout(this._searchTimer)
                this._searchTimer = setTimeout(() => {
                    this.load()
                }, 300)
            }
        }
    },
    data() {
        return {
            fluides: fluidesList,
            selectedFluide: null,
            showErrors: false,
            search: '',
            showDropdown: false,
            filteredFluides: [],

            bouteilles: [],
            current: null,
            form: {},
            gaz: {},
            emplacement: {},
            movements: [],
            emplacements: [],

            formAdresseRecup: {},

            numTrackDechets: '',

            searchFilter: '',
            showDropdownFilters: false,
            filteredFluidesFilter: [],
            filters: {
                numero: null,
                type_gaz: null,
                etat: null,
                emplacement_id: null,
                is_recycle: 1, // par défaut non recyclées
                date_debut: null,
                date_fin: null,
            },

            exportFilters: {
                type_export: 'mouvements',
                numero: null,
                type_gaz: null,
                etat: null,
                emplacement_id: null,
                is_recycle: null,
                date_debut: null,
                date_fin: null,
            },

            etatList: ['charge', 'récupération'],

            adressesList: [],
            displayNewAdresse: false,
            
            loadingList: false,
            loadingChangeEmplacement: false,
            loadingEditAddBouteille: false,
            loadingGetMovements: false,
            loadingAddGaz: false,
            loadingTrackDechets: false,
            loadingExport: false,
        };
    },

    methods: {
        async load() {
            this.loadingList = true
            let res = await this.$request.get('/bouteilles', {
                params: {
                    ...this.filters,
                }
            })
            this.bouteilles = res;
            this.loadingList = false
        },

        openCreate() {
            this.form = {
                numero: '',
                type_gaz: '',
                poids_max: null,
                recuperation_adresse_id: null,
                emplacement_id: null,
                etat: 'charge',
            };
            this.formAdresseRecup = {}
            this.search = '';
            this.selectedFluide = null;
            if (!this.adressesList.length) {
                this.displayNewAdresse = true;
            } else {
                this.displayNewAdresse = false;
            }
            if (this.emplacements.length === 1) {
                this.form.emplacement_id = this.emplacements[0].id;
            }
            this.$modal.show('bouteille-form');

            if (this.adressesList.length === 1) {
                this.form.recuperation_adresse_id = this.adressesList[0].id;
            }
        },

        openEdit(b) {
            this.form = { ...b };
            this.formAdresseRecup = { ...b.adresse_recuperation }
            this.selectedFluide = this.fluides.find(f => f.code === b.type_gaz) || null;
            this.displayNewAdresse = false;
            this.search = this.selectedFluide ? this.selectedFluide.code : '';
            this.$modal.show('bouteille-form');
        },

        async submitForm() {
            let validationData = [
                'inp-numero:required|min=2',
                'inp-type-gaz:required|min=2',
                'inp-type:required',
                'inp-poids-max:required|number|min=1',
            ]
            if (!this.form.recuperation_adresse_id && this.displayNewAdresse) {
                validationData.push(
                    'inp-adresse1:required|min=5',
                    'inp-code-postal:required|postal',
                    'inp-ville:required|min=2',
                    'inp-pays:required|min=2',
                )
            }
            if (!this.displayNewAdresse && !this.form.recuperation_adresse_id) {
                this.$toast.error('Veuillez sélectionner ou ajouter une adresse de recyclage');
                return;
            }
            const validation = this.$inpError(validationData);
            if (!validation) {
                this.$toast.error('Formulaire invalide')
                return
            }
            
            if (this.form.poids_max < 0) {
                this.$toast.error('La quantité de gaz ne peut pas être négative');
                return;
            }

            if (this.form.poids_actuel) {
                if (this.form.poids_actuel < 0) {
                    this.$toast.error('La quantité actuelle de gaz ne peut pas être négative');
                    return;
                }
                if (this.form.poids_actuel > this.form.poids_max) {
                    this.$toast.error('La quantité actuelle de gaz ne peut pas dépasser la capacité maximale de la bouteille');
                    return;
                }
            }

            this.loadingEditAddBouteille = true

            if (!this.form.recuperation_adresse_id) {
                this.formAdresseRecup.no_client = 1
                let responseAdresse = await this.$request.post(`/clients/0/adresses`, this.formAdresseRecup)
                this.form.recuperation_adresse_id = responseAdresse.id
            }
            let url = this.form.id ? `/bouteilles/${this.form.id}/update` : '/bouteilles';
            await this.$request.post(url, this.form)

            this.loadingEditAddBouteille = false

            this.$modal.hide('bouteille-form');
            this.load();

            this.getAdresses()
        },

        openGaz(b) {
            this.current = b;
            this.gaz = { quantite: null };
            this.$modal.show('gaz-form');
        },

        async submitGaz() {
            const validation = this.$inpError([
                'inp-qtt-gaz:required',
            ]);

            if (!validation) {
                this.$toast.error('Formulaire invalide')
                return
            }

            // Il y a assez de gaz dans la bouteille pour un retrait
            if (this.current.etat == 'charge') {
                if (this.current.poids_actuel - this.gaz.quantite < 0) {
                    this.$toast.error('La quantité dépasse le poids maximum de la bouteille');
                    return;
                }
            }
            
            // Il y a assez de place dans la bouteille pour une récupération
            if (this.current.etat == 'récupération') {
                if (this.gaz.quantite + this.current.poids_actuel > this.current.poids_max) {
                    this.$toast.error('La bouteille ne peut pas contenir autant de gaz');
                    return;
                }
            }

            this.loadingAddGaz = true;

            // Signe de la quantité selon le type d'opération
            if (this.current.etat == 'charge') {
                this.gaz.quantite = -this.gaz.quantite;
            }

            await this.$request.post(`/bouteilles/${this.current.id}/gaz`, this.gaz);
            this.$modal.hide('gaz-form');
            await this.load();

            this.loadingAddGaz = false;
        },

        openEmplacement(b) {
            this.current = b;
            this.emplacement = { emplacement_id: b.emplacement_id };
            this.$modal.show('emplacement-form');
        },

        async submitEmplacement() {
            this.loadingChangeEmplacement = true
            let response = await this.$request.post(`/bouteilles/${this.current.id}/emplacement`, this.emplacement)
            await this.load();
            this.$modal.hide('emplacement-form');
            this.loadingChangeEmplacement = false
        },

        async openMovements(b) {
            this.$modal.show('movements');
            this.loadingGetMovements = true;
            let res = await this.$request.get(`/bouteilles/${b.id}/movements`)
            this.movements = res;
            this.current = b;
            this.loadingGetMovements = false;
        },
        async getEmplacements() {
            let response = await this.$request.get("/stock/locations");
            this.emplacements = response.locations || [];
        },

        openRecycle(b) {
            this.current = b;
            this.$modal.show('recycle-form');
        },

        async submitRecycle() {
            const validation = this.$inpError([
                'inp-track-dechets:required|min=2',
            ]);

            if (!validation) {
                this.$toast.error('Formulaire invalide')
                return
            }

            this.loadingTrackDechets = true;

            await this.$request.post(`/bouteilles/${this.current.id}/recycle`, {
                num_track_dechets: this.numTrackDechets,
            });

            this.$modal.hide('recycle-form');
            await this.load();

            this.loadingTrackDechets = false;
        },

        filterFluides() {
            const s = this.search.toLowerCase().trim();
            if (!s) {
                this.filteredFluides = [];
                return;
            }
            this.filteredFluides = this.fluides.filter(f =>
                f.code.toLowerCase().includes(s) ||
                f.nom.toLowerCase().includes(s)
            ).slice(0, 10);
        },

        filterFluidesFilter() {
            const s = this.searchFilter.toLowerCase().trim();
            if (!s) {
                this.filteredFluidesFilter = [];
                this.filters.type_gaz = null;
                return;
            }
            this.filteredFluidesFilter = this.fluides.filter(f =>
                f.code.toLowerCase().includes(s) ||
                f.nom.toLowerCase().includes(s)
            ).slice(0, 10);
        },

        selectFluide(f) {
            this.selectedFluide = f;
            this.form.type_gaz = f.code;
            this.search = f.code;
            this.showDropdown = false;
        },

        selectFluideFilter(f) {
            this.filters.type_gaz = f.code;
            this.searchFilter = f.code;
            this.showDropdownFilters = false;
        },

        exportMovementsCsv() {
            const headers = ['Action', 'Détails', 'Utilisateur', 'Date', 'CERFA'];
            const rows = this.movements.map(m => [
                m.action,
                m.details,
                m.account ? `${m.account.prenom} ${m.account.nom}` : '-',
                new Date(m.created_at).toLocaleString(),
                m.cerfa ? m.cerfa.document_url : '-'
            ]);
            let csvContent = 'data:text/csv;charset=utf-8,';
            csvContent += headers.join(',') + '\n';
            rows.forEach(row => {
                csvContent += row.map(field => `"${field}"`).join(',') + '\n';
            });

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement('a');
            link.setAttribute('href', encodedUri);
            link.setAttribute('download', `bouteille_${this.current.numero}_movements.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        openExportModal() {
            let todayLastYear = new Date();
            todayLastYear.setFullYear(todayLastYear.getFullYear() - 1);
            todayLastYear = todayLastYear.toISOString().split('T')[0];
            let today = new Date().toISOString().split('T')[0];
            this.exportFilters = {
                type_export: 'mouvements',
                date_debut: todayLastYear,
                date_fin: today,
                is_recycle: null,
                emplacement_id: null,
                etat: null,
            }
            this.$modal.show('export-csv-modal')
        },

        async exportCsv() {
            this.loadingExport = true

            try {
                const response = await axios.post(
                    env.apiUrl + '/bouteilles/export/csv',
                    {
                        ...this.exportFilters,
                    },
                    {
                        responseType: 'blob',
                        headers: {
                            'Authorization': 'Bearer ' + env.authToken,
                        }
                    }
                )

                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
                const url = window.URL.createObjectURL(blob)

                let fileName = 'export.csv'
                const cd = response.headers['content-disposition']
                if (cd) {
                    const match = cd.match(/filename="?([^"]+)"?/)
                    if (match) fileName = match[1]
                }

                const link = document.createElement('a')
                link.href = url
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                window.URL.revokeObjectURL(url)

                this.$toast.success('Export CSV généré avec succès')
                this.$modal.hide('export-csv-modal')
            } catch (e) {
                console.error(e)
                this.$toast.error('Erreur lors de la génération du CSV')
            }

            this.loadingExport = false
        },

        async getAdresses() {
            let response = await this.$request.post('/bouteilles/adresses-recyclage')
            this.adressesList = response.adresses
        }

    },

    mounted() {
        this.load();
        this.getEmplacements();
        this.getAdresses();
    },
};
</script>
